import { createContext, useContext, useState, type ReactNode } from 'react';
import { Alert, Button, Collapse, Input, Select, Space, Table, Tag, type TableProps } from 'antd';
import { beijingDay, runListQuery, type FilterRow, type ListQuery } from './query';

type Choice = { key: string; label: string };
export type ListViewConfig = {
  key: string;
  unit?: '人' | '条';
  facets: Choice[];
  dates: Choice[];
  sorts: Choice[];
  groups: Choice[];
  defaults: ListQuery;
  quickStatus?: string;
};
type SavedQueries = { queries: Record<string, ListQuery>; save: (key: string, query: ListQuery) => void };
const FilterScope = createContext<SavedQueries | null>(null);
export function FilterScopeProvider({ scopeKey, children }: { scopeKey: string; children: ReactNode }) {
  return <Scope key={scopeKey}>{children}</Scope>;
}
function Scope({ children }: { children: ReactNode }) {
  const [queries, setQueries] = useState<Record<string, ListQuery>>({});
  return (
    <FilterScope.Provider value={{ queries, save: (key, query) => setQueries((old) => ({ ...old, [key]: query })) }}>
      {children}
    </FilterScope.Provider>
  );
}

export function datePreset(preset: string, now = new Date()) {
  const today = beijingDay(now.toISOString())!;
  const date = new Date(`${today}T00:00:00Z`);
  const iso = (value: Date) => value.toISOString().slice(0, 10);
  if (preset === '本周') {
    date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
    const from = iso(date);
    date.setUTCDate(date.getUTCDate() + 6);
    return { from, to: iso(date) };
  }
  if (preset === '本月') {
    const from = `${today.slice(0, 7)}-01`;
    date.setUTCMonth(date.getUTCMonth() + 1, 0);
    return { from, to: iso(date) };
  }
  if (preset === '近30天') date.setUTCDate(date.getUTCDate() - 29);
  return { from: iso(date), to: today };
}
type Props<T extends object> = TableProps<T> & {
  config: ListViewConfig;
  project: (row: T, index: number) => FilterRow;
  onQueryChange?: () => void;
  renderSummary?: (rows: T[]) => ReactNode;
  error?: string;
};
export function FilteredTable<T extends object>(props: Props<T>) {
  return <ListBody key={props.config.key} {...props} />;
}
function ListBody<T extends object>({
  config,
  project,
  dataSource = [],
  onQueryChange,
  renderSummary,
  error,
  pagination,
  ...tableProps
}: Props<T>) {
  const scope = useContext(FilterScope);
  const [local, setLocal] = useState(config.defaults);
  const query = scope?.queries[config.key] ?? local;
  const [revision, setRevision] = useState(0);
  const [more, setMore] = useState(false);
  const [pageSize, setPageSize] = useState((pagination && pagination.pageSize) || 10);
  const projections = dataSource.map(project);
  const originals = new Map(projections.map((row, index) => [row.id, dataSource[index]]));
  const result = runListQuery(projections, query);
  const resolve = (ids: string[]) => ids.map((id) => originals.get(id)!);
  const change = (next: ListQuery) => {
    if (scope) scope.save(config.key, next);
    else setLocal(next);
    setRevision((value) => value + 1);
    onQueryChange?.();
  };
  const choices = (key: string) =>
    [...new Set(projections.map((row) => row.facets[key] || '未分配'))].sort((a, b) => a.localeCompare(b, 'zh-CN'));
  const facetLabel = (key: string, value: string) => {
    const label = projections.find((row) => row.facets[key] === value)?.facetLabels?.[key] || value;
    const ambiguous = projections.some(
      (row) => row.facets[key] !== value && (row.facetLabels?.[key] || row.facets[key]) === label
    );
    return ambiguous ? `${label}（${value}）` : label;
  };
  const selectOptions = (items: Choice[]) => items.map((item) => ({ value: item.key, label: item.label }));
  const unit = config.unit ?? '条';
  const renderTable = (ids: string[], key: string) => (
    <Table<T>
      {...tableProps}
      key={`${revision}:${key}`}
      dataSource={resolve(ids)}
      locale={{
        ...tableProps.locale,
        emptyText: error
          ? '数据加载失败'
          : result.error
            ? '请修正筛选条件'
            : dataSource.length
              ? '没有符合条件的记录，可清空筛选'
              : (tableProps.locale?.emptyText ?? '暂无记录')
      }}
      pagination={
        pagination === false
          ? false
          : {
              hideOnSinglePage: true,
              showSizeChanger: true,
              ...pagination,
              current: undefined,
              defaultCurrent: 1,
              pageSize,
              onChange: (_, size) => setPageSize(size)
            }
      }
    />
  );
  return (
    <div className="filtered-list">
      <div className="list-filter-bar" aria-label="列表筛选">
        <Input
          aria-label="搜索"
          placeholder="搜索姓名、部门、职位或名称"
          allowClear
          value={query.keyword}
          onChange={(event) => change({ ...query, keyword: event.target.value })}
          style={{ width: 260 }}
        />
        {config.facets.map((facet, index) => (
          <Select
            key={facet.key}
            aria-label={facet.label}
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{
              minWidth: 150,
              display: index > 2 && !more && !query.selected[facet.key]?.length ? 'none' : undefined
            }}
            placeholder={facet.label}
            value={query.selected[facet.key] ?? []}
            options={choices(facet.key).map((value) => ({ value, label: facetLabel(facet.key, value) }))}
            onChange={(values: string[]) => change({ ...query, selected: { ...query.selected, [facet.key]: values } })}
          />
        ))}
        {config.facets.length > 3 && <Button onClick={() => setMore(!more)}>{more ? '收起条件' : '更多筛选'}</Button>}
        {config.dates.length > 0 && (
          <>
            <Select
              aria-label="日期类型"
              value={query.date.field}
              options={selectOptions(config.dates)}
              onChange={(field) => change({ ...query, date: { ...query.date, field } })}
              style={{ minWidth: 150 }}
            />
            <Select
              aria-label="日期模式"
              value={query.date.mode}
              options={[
                { value: 'ALL', label: '全部日期' },
                { value: 'RANGE', label: '指定范围' },
                { value: 'MISSING', label: '未记录日期' }
              ]}
              onChange={(mode) => change({ ...query, date: { ...query.date, mode } })}
              style={{ minWidth: 130 }}
            />
            {query.date.mode === 'RANGE' && (
              <>
                <Input
                  aria-label="开始日期"
                  type="date"
                  value={query.date.from ?? ''}
                  onChange={(e) => change({ ...query, date: { ...query.date, from: e.target.value } })}
                  style={{ width: 155 }}
                />
                <span>至</span>
                <Input
                  aria-label="结束日期"
                  type="date"
                  value={query.date.to ?? ''}
                  onChange={(e) => change({ ...query, date: { ...query.date, to: e.target.value } })}
                  style={{ width: 155 }}
                />
              </>
            )}
            <Select
              aria-label="快捷时间"
              placeholder="快捷时间"
              value={undefined as string | undefined}
              style={{ width: 125 }}
              options={['今天', '本周', '本月', '近30天'].map((value) => ({ value, label: value }))}
              onChange={(preset) => change({ ...query, date: { ...query.date, mode: 'RANGE', ...datePreset(preset) } })}
            />
          </>
        )}
        {config.sorts.length > 0 && (
          <>
            <Select
              aria-label="排序字段"
              value={query.sort.field}
              options={selectOptions(config.sorts)}
              onChange={(field) => change({ ...query, sort: { ...query.sort, field } })}
              style={{ minWidth: 135 }}
            />
            <Select
              aria-label="排序方向"
              value={query.sort.direction}
              options={[
                { value: 'asc', label: '升序' },
                { value: 'desc', label: '降序' }
              ]}
              onChange={(direction) => change({ ...query, sort: { ...query.sort, direction } })}
              style={{ width: 90 }}
            />
          </>
        )}
        {config.groups.length > 0 && (
          <Select
            aria-label="分组查看"
            value={query.groupBy}
            options={[{ value: '', label: '普通列表' }, ...selectOptions(config.groups)]}
            onChange={(groupBy) => change({ ...query, groupBy })}
            style={{ minWidth: 135 }}
          />
        )}
        <Button onClick={() => change(config.defaults)}>清空筛选</Button>
      </div>
      {error && <Alert type="error" message={error} />}
      {result.error && <Alert type="warning" message={result.error} />}
      <Space wrap className="list-filter-summary">
        <span>{`筛选后 ${result.total} ${unit} / 本分类共 ${dataSource.length} ${unit}`}</span>
        {query.keyword && (
          <Tag closable onClose={() => change({ ...query, keyword: '' })}>
            搜索：{query.keyword}
          </Tag>
        )}
        {Object.entries(query.selected).flatMap(([key, values]) =>
          values.map((value) => (
            <Tag
              key={`${key}:${value}`}
              closable
              onClose={() =>
                change({ ...query, selected: { ...query.selected, [key]: values.filter((item) => item !== value) } })
              }
            >
              {config.facets.find((f) => f.key === key)?.label}：{facetLabel(key, value)}
            </Tag>
          ))
        )}
        {query.date.mode !== 'ALL' && (
          <Tag closable onClose={() => change({ ...query, date: { ...query.date, mode: 'ALL' } })}>
            {config.dates.find((d) => d.key === query.date.field)?.label}：
            {query.date.mode === 'MISSING'
              ? '未记录日期'
              : `${query.date.from || '未选'} 至 ${query.date.to || '未选'}`}
          </Tag>
        )}
      </Space>
      {config.quickStatus && (
        <Space wrap className="list-status-shortcuts">
          {[undefined, ...choices(config.quickStatus)].map((value) => {
            const key = config.quickStatus!;
            const count = runListQuery(projections, {
              ...query,
              selected: { ...query.selected, [key]: value ? [value] : [] }
            }).total;
            const active = value
              ? query.selected[key]?.length === 1 && query.selected[key][0] === value
              : !query.selected[key]?.length;
            return (
              <Button
                key={value ?? 'all'}
                type={active ? 'primary' : 'default'}
                onClick={() => change({ ...query, selected: { ...query.selected, [key]: value ? [value] : [] } })}
              >
                {value ? facetLabel(key, value) : '全部'} {count}
              </Button>
            );
          })}
        </Space>
      )}
      {renderSummary?.(resolve(result.ids))}
      {query.groupBy && result.groups.length ? (
        <Collapse
          key={revision}
          defaultActiveKey={result.groups.map((group) => group.key)}
          items={result.groups.map((group) => ({
            key: group.key,
            label: `${facetLabel(query.groupBy, group.key)} · ${group.ids.length} ${unit}`,
            children: renderTable(group.ids, group.key)
          }))}
        />
      ) : (
        renderTable(result.ids, 'all')
      )}
    </div>
  );
}
